import { accessJson, debounce, ObjExcept } from '@florezjaramillo/florezjaramillo-core'
import { ReactiveSet } from '@solid-primitives/set'
import type { Accessor, JSX } from 'solid-js'
import { batch, createEffect, createMemo, createSignal, For, mergeProps, on, onCleanup, Show, splitProps, untrack } from 'solid-js'
import { createStore } from 'solid-js/store'
import { twMerge } from 'tailwind-merge'
import type { MainColorsType } from '~/types/components'
import type {
  ColumnType,
  FilterChipItemType,
  PageChangeEventType,
  PaginationInfoType,
  PerPageChangeEventType,
  SortChangeEventType,
  SortDirectionType,
  SortInfoType,
  StickySideType,
  TablePropsType
} from '~/types/Table'
import Avatar from './Avatar'
import Button from './Button'
import Card from './Card'
import Checkbox from './Checkbox'
import Chip from './Chip'
import Icon from './Icon'
import Menu from './Menu'
import Select from './Select'
import Skeleton from './Skeleton'
import Switch from './Switch'
import Tooltip from './Tooltip'

export type { ColumnType, PageChangeEventType, PaginationInfoType, PerPageChangeEventType, SortChangeEventType, SortDirectionType, SortInfoType, TablePropsType }

const DEFAULT_PER_PAGE_OPTIONS = [5, 10, 15, 30, 50, 100] as const
const MAIN_COLORS: readonly MainColorsType[] = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'default'] as const

/** Fila seleccionada: tinte suave segun el color de seleccion (tokens semanticos). */
const TR_SELECTED_COLORS: Readonly<Record<MainColorsType, string>> = {
  primary: 'bg-primary-soft',
  danger: 'bg-danger-soft',
  success: 'bg-success-soft',
  warning: 'bg-warning-soft',
  info: 'bg-info-soft',
  secondary: 'bg-surface-hover',
  default: 'bg-surface-hover'
} as const

/** Banner de selección: fondo suave + texto sobre suave. */
const BANNER_COLORS: Readonly<Record<MainColorsType, string>> = {
  primary: 'bg-primary-soft text-on-primary-soft',
  danger: 'bg-danger-soft text-on-danger-soft',
  success: 'bg-success-soft text-on-success-soft',
  warning: 'bg-warning-soft text-on-warning-soft',
  info: 'bg-info-soft text-on-info-soft',
  secondary: 'bg-surface-muted text-foreground',
  default: 'bg-surface-muted text-foreground'
} as const

/** Color de relleno sólido (para barras de progreso). */
const SOLID_BG: Readonly<Record<MainColorsType, string>> = {
  primary: 'bg-primary',
  danger: 'bg-danger',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  secondary: 'bg-foreground-subtle',
  default: 'bg-foreground-subtle'
} as const

const TH_BASE_CLASS = 'bg-surface-muted text-foreground-subtle text-left text-sm font-semibold p-4 whitespace-nowrap border-b-[1.5px] border-dashed border-border'
const TR_BASE_CLASS = 'hover:bg-surface-hover transition-colors'
const TD_BASE_CLASS = 'p-4 text-sm text-foreground-muted align-middle border-b-[1.5px] border-dashed border-border'

/**
 * Obtiene el sortKey de una columna de forma segura
 */
const getColumnSortKey = <T extends object>(column: ColumnType<T>): string | null => {
  if (!column.sortable) return null
  if (column.sortKey) return column.sortKey
  if (typeof column.accessor === 'string') return column.accessor
  return null
}

/**
 * Compara dos valores para ordenamiento con soporte multi-tipo
 */
const compareValues = (aValue: unknown, bValue: unknown, direction: 'asc' | 'desc'): number => {
  if (aValue == null && bValue == null) return 0
  if (aValue == null) return direction === 'asc' ? 1 : -1
  if (bValue == null) return direction === 'asc' ? -1 : 1

  let comparison = 0

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    comparison = aValue - bValue
  } else if (typeof aValue === 'string' && typeof bValue === 'string') {
    comparison = aValue.localeCompare(bValue, 'es', { sensitivity: 'base' })
  } else if (aValue instanceof Date && bValue instanceof Date) {
    comparison = aValue.getTime() - bValue.getTime()
  } else {
    comparison = String(aValue).localeCompare(String(bValue), 'es')
  }

  return direction === 'asc' ? comparison : -comparison
}

/**
 * Texto de paginación por defecto
 */
const defaultPaginationText = (info: PaginationInfoType): string => {
  if (info.total === 0) return 'Sin resultados'
  return `${info.from + 1}–${info.to + 1} de ${info.total}`
}

/**
 * Calcula el offset sticky para una columna
 */
const Table = <T extends object>(props: TablePropsType<T>): JSX.Element => {
  const [local] = splitProps(props, [
    'data',
    'columns',
    'keyField',
    'striped',
    'selectable',
    'selectableColor',
    'selectableAction',
    'selectableClass',
    'onSelectionChange',
    'pagination',
    'page',
    'perPage',
    'perPageOptions',
    'total',
    'serverSide',
    'onPageChange',
    'onPerPageChange',
    'paginationText',
    'hidePerPageSelector',
    'hidePaginationInfo',
    'hover',
    'dense',
    'showDense',
    'onDenseChange',
    'emptyMessage',
    'rowClass',
    'onRowClick',
    'actions',
    'actionsPosition',
    'actionsSticky',
    'actionsLabel',
    'columnMinWidth',
    'pinnable',
    'itemsPerPage',
    'renderTopHeader',
    'searchable',
    'searchPlaceholder',
    'searchValue',
    'onSearch',
    'searchFields',
    'globalSearch',
    'toolbarActions',
    'filterChips',
    'showFilters',
    'filterOptions',
    'activeFilter',
    'filterField',
    'onFilterChange',
    'onDeleteSelected',
    'title',
    'subtitle',
    'headerActions',
    'class',
    'loading',
    'maxHeight',
    'stickyHeader',
    'headSticky',
    'headStickyOffset',
    'columnGroups',
    'sortColumn',
    'sortDirection',
    'onSortChange'
  ])

  const merged = mergeProps(
    {
      pagination: true,
      keyField: 'id' as keyof T,
      selectableColor: 'danger' as MainColorsType,
      page: 1,
      perPage: 15,
      perPageOptions: DEFAULT_PER_PAGE_OPTIONS as unknown as number[],
      serverSide: false,
      hidePerPageSelector: false,
      hidePaginationInfo: false,
      loading: false,
      stickyHeader: false,
      // Las acciones van a la izquierda y fijas: es lo primero que se busca y
      // así no hay que scrollear hasta el final para pulsarlas.
      actionsPosition: 'left' as const,
      actionsSticky: true,
      actionsLabel: 'Acciones',
      // Con muchas columnas es mejor desplazarse en horizontal que apretujarlas
      // hasta que no se lea nada. Cada columna puede pisar esto con su minWidth.
      columnMinWidth: 150,
      pinnable: true,
      searchPlaceholder: 'Buscar...',
      sortColumn: null as string | null,
      sortDirection: null as SortDirectionType
    },
    local
  )

  interface TableState {
    dense: boolean
    currentPage: number
    currentPerPage: number
    sortColumn: string | null
    sortDirection: SortDirectionType
  }

  const [state, setState] = createStore<TableState>({
    dense: merged.dense ?? false,
    currentPage: merged.page,
    currentPerPage: merged.perPage,
    sortColumn: merged.sortColumn,
    sortDirection: merged.sortDirection
  })

  // Sincroniza el estado denso si la prop controlada cambia.
  createEffect(
    on(
      () => merged.dense,
      value => {
        if (typeof value === 'boolean') setState('dense', value)
      },
      { defer: true }
    )
  )

  // Búsqueda y filtros (estado interno, sincronizado con props controladas)
  const [searchQuery, setSearchQuery] = createSignal(merged.searchValue ?? '')
  const [activeTab, setActiveTab] = createSignal(merged.activeFilter ?? merged.filterOptions?.[0]?.value ?? '')

  const selected = new ReactiveSet<unknown>()

  // Si una fila seleccionada desaparece de los datos (la borraron, cambió la
  // página o los filtros), deja de contar como seleccionada: evita banners
  // colgados con ids que ya no existen. La selección es por página: "seleccionar
  // todo" marca solo las filas visibles, así que al navegar se limpia igual.
  createEffect(
    on(
      () => merged.data,
      data => {
        const rows = data ?? []
        const valid = new Set<unknown>(rows.map(row => row[merged.keyField as keyof T]))
        for (const key of [...selected]) {
          if (!valid.has(key)) selected.delete(key)
        }
      },
      { defer: true }
    )
  )

  createEffect(
    on(
      () => [merged.page, merged.perPage, merged.sortColumn, merged.sortDirection] as const,
      ([newPage, newPerPage, newSortColumn, newSortDirection]) => {
        batch(() => {
          if (newPage !== state.currentPage) setState('currentPage', newPage)
          if (newPerPage !== state.currentPerPage) setState('currentPerPage', newPerPage)
          if (newSortColumn !== state.sortColumn) setState('sortColumn', newSortColumn)
          if (newSortDirection !== state.sortDirection) setState('sortDirection', newSortDirection)
        })
      },
      { defer: true }
    )
  )

  // Sincroniza props controladas de búsqueda/filtro
  createEffect(
    on(
      () => merged.searchValue,
      v => v !== undefined && setSearchQuery(v),
      { defer: true }
    )
  )
  createEffect(
    on(
      () => merged.activeFilter,
      v => v !== undefined && setActiveTab(v),
      { defer: true }
    )
  )

  // Al buscar/filtrar volvemos a la primera página (solo en modo cliente)
  createEffect(
    on(
      () => [searchQuery(), activeTab()] as const,
      () => {
        if (!merged.serverSide && state.currentPage !== 1) setState('currentPage', 1)
      },
      { defer: true }
    )
  )

  /** Columnas con accessor de tipo string (claves usadas por la búsqueda por defecto). */
  const stringAccessorKeys = createMemo(() => merged.columns?.filter(c => typeof c.accessor === 'string').map(c => c.accessor as keyof T) ?? [])

  /** Aplica el filtro de tabs cuando hay filterField y no es serverSide. */
  const tabFilteredData = createMemo<T[]>(() => {
    const data = merged.data ?? []
    if (merged.serverSide || !merged.filterField) return data
    const active = activeTab()
    const firstValue = merged.filterOptions?.[0]?.value
    // La primera opción se considera "todos" → sin filtro.
    if (!active || active === firstValue) return data
    const field = merged.filterField
    return data.filter(item => String(item[field]) === active)
  })

  /** Aplica la búsqueda en cliente (a menos que se delegue vía onSearch). */
  const searchedData = createMemo<T[]>(() => {
    const data = tabFilteredData()
    const q = searchQuery().trim().toLowerCase()
    if (merged.serverSide || merged.onSearch || !q) return data

    if (merged.globalSearch) return data.filter(item => merged.globalSearch!(item, q))

    const keys = merged.searchFields ?? stringAccessorKeys()
    if (keys.length === 0) return data
    return data.filter(item =>
      keys.some(key => {
        const value = accessJson(item, String(key))
        return value != null && String(value).toLowerCase().includes(q)
      })
    )
  })

  const totalItems: Accessor<number> = createMemo(() => (merged.serverSide && merged.total !== undefined ? merged.total : (searchedData().length ?? 0)))

  const paginationInfo = createMemo<PaginationInfoType>(() => {
    const total = totalItems()
    const perPage = state.currentPerPage
    const totalPages = Math.max(1, Math.ceil(total / perPage))
    const page = Math.min(Math.max(1, state.currentPage), totalPages)
    const from = total > 0 ? (page - 1) * perPage : 0
    const to = Math.min(from + perPage - 1, total - 1)

    return { page, perPage, total, totalPages, from, to }
  })

  const sortedData = createMemo<T[]>(() => {
    const data = searchedData()

    if (merged.serverSide || !state.sortColumn || !state.sortDirection) {
      return data
    }

    const sortKey = state.sortColumn
    const direction = state.sortDirection

    return [...data].sort((a, b) => {
      const aValue = accessJson(a, sortKey)
      const bValue = accessJson(b, sortKey)
      return compareValues(aValue, bValue, direction)
    })
  })

  const paginatedData = createMemo<T[]>(() => {
    if (!merged.pagination || merged.serverSide) return sortedData()
    const info = paginationInfo()
    return sortedData().slice(info.from, info.to + 1)
  })

  const displayedData = createMemo(() => paginatedData())

  const hasNextPage = createMemo(() => paginationInfo().page < paginationInfo().totalPages)
  const hasPrevPage = createMemo(() => paginationInfo().page > 1)

  const selectableColor = createMemo<MainColorsType>(() => (MAIN_COLORS.includes(merged.selectableColor!) ? merged.selectableColor! : 'primary'))

  const indeterminate = createMemo(() => selected.size > 0 && selected.size < (displayedData()?.length ?? 0))
  const allSelected = createMemo(() => displayedData().length > 0 && selected.size === displayedData().length)

  const perPageSelectOptions = createMemo(() =>
    merged.perPageOptions?.map(value => ({
      value,
      title: `${value}`
    }))
  )

  /** Tabs con contadores autocalculados cuando hay filterField. */
  const filterTabs = createMemo(() => {
    const options = merged.filterOptions ?? []
    const field = merged.filterField
    const data = merged.data ?? []
    return options.map((opt, i) => {
      if (opt.count !== undefined || merged.serverSide || !field) return opt
      const count = i === 0 ? data.length : data.filter(item => String(item[field]) === opt.value).length
      return { ...opt, count }
    })
  })

  const totalColumns = createMemo(() => {
    let cols = merged.columns?.length ?? 0
    if (merged.selectable) cols += 1
    if (merged.actions) cols += 1
    return cols
  })

  const containerStyle = createMemo((): JSX.CSSProperties => {
    if (!merged.maxHeight) return {}
    return {
      'max-height': typeof merged.maxHeight === 'number' ? `${merged.maxHeight}px` : merged.maxHeight,
      'overflow-y': 'auto'
    }
  })

  const isRowSelected = (key: unknown): boolean => selected.has(key)

  const getThClass = createMemo(
    () =>
      (extraClass = '') =>
        twMerge(TH_BASE_CLASS, state.dense ? 'py-2' : '', extraClass)
  )

  const getTrClass = createMemo(
    () =>
      (extraClass = '') =>
        twMerge(TR_BASE_CLASS, extraClass)
  )

  const emitSelectionChange = debounce(() => {
    merged.onSelectionChange?.(selected)
  }, 50)

  createEffect(() => {
    const _ = selected.size
    emitSelectionChange()
  })

  const handleSearchInput = (value: string): void => {
    setSearchQuery(value)
    merged.onSearch?.(value)
  }

  const handleFilterClick = (value: string): void => {
    if (value === activeTab()) return
    setActiveTab(value)
    merged.onFilterChange?.(value)
  }

  const handlePageChange = (newPage: number): void => {
    const info = paginationInfo()
    const validPage = Math.min(Math.max(1, newPage), info.totalPages)

    if (validPage === state.currentPage) return

    setState('currentPage', validPage)

    if (merged.onPageChange) {
      merged.onPageChange({
        page: validPage,
        perPage: info.perPage,
        info: {
          ...info,
          page: validPage,
          from: (validPage - 1) * info.perPage,
          to: Math.min(validPage * info.perPage - 1, info.total - 1)
        }
      })
    }
  }

  const handlePerPageChange = (newPerPage: number): void => {
    if (newPerPage === state.currentPerPage) return

    const previousPerPage = state.currentPerPage
    const info = paginationInfo()
    const currentFirstItem = info.from
    const newPage = Math.max(1, Math.floor(currentFirstItem / newPerPage) + 1)
    const newTotalPages = Math.max(1, Math.ceil(info.total / newPerPage))

    batch(() => {
      setState('currentPerPage', newPerPage)
      setState('currentPage', newPage)
    })

    const newInfo: PaginationInfoType = {
      page: newPage,
      perPage: newPerPage,
      total: info.total,
      totalPages: newTotalPages,
      from: (newPage - 1) * newPerPage,
      to: Math.min(newPage * newPerPage - 1, info.total - 1)
    }

    merged.onPerPageChange?.({ perPage: newPerPage, previousPerPage, info: newInfo })

    if (merged.onPageChange && newPage !== untrack(() => state.currentPage)) {
      merged.onPageChange({ page: newPage, perPage: newPerPage, info: newInfo })
    }
  }

  const handleSortChange = (column: ColumnType<T>): void => {
    const sortKey = getColumnSortKey(column)
    if (!sortKey) return

    const previousSort: SortInfoType | null = state.sortColumn ? { column: state.sortColumn, direction: state.sortDirection } : null

    let newDirection: SortDirectionType
    let newColumn: string | null

    if (state.sortColumn === sortKey) {
      if (state.sortDirection === 'asc') {
        newDirection = 'desc'
        newColumn = sortKey
      } else if (state.sortDirection === 'desc') {
        newDirection = null
        newColumn = null
      } else {
        newDirection = 'asc'
        newColumn = sortKey
      }
    } else {
      newDirection = 'asc'
      newColumn = sortKey
    }

    batch(() => {
      setState('sortColumn', newColumn)
      setState('sortDirection', newDirection)
      if (!merged.serverSide && state.currentPage !== 1) {
        setState('currentPage', 1)
      }
    })

    if (merged.onSortChange) {
      const newSort: SortInfoType | null = newColumn ? { column: newColumn, direction: newDirection } : null
      merged.onSortChange({ sort: newSort, previousSort })
    }
  }

  const goToFirstPage = () => handlePageChange(1)
  const goToLastPage = () => handlePageChange(paginationInfo().totalPages)
  const goToNextPage = () => handlePageChange(state.currentPage + 1)
  const goToPrevPage = () => handlePageChange(state.currentPage - 1)

  const handleRowToggle = (key: unknown): void => {
    if (selected.has(key)) {
      selected.delete(key)
    } else {
      selected.add(key)
    }
  }

  const handleSelectAll = (): void => {
    if (allSelected()) {
      selected.clear()
      return
    }
    displayedData()?.forEach((item: T) => {
      selected.add(item[merged.keyField as keyof T])
    })
  }

  const handleDenseChange = (checked: boolean): void => {
    setState('dense', checked)
    merged.onDenseChange?.(checked)
  }

  /**
   * Ancho real de la columna de acciones. Se mide en el DOM porque las demás
   * columnas fijas necesitan saber cuánto desplazarse para no quedar debajo.
   */
  const [actionsWidth, setActionsWidth] = createSignal(0)

  const measureActions = (el: HTMLTableCellElement) => {
    const update = () => setActionsWidth(el.getBoundingClientRect().width)
    // Medir en el siguiente frame: leer layout dentro del ref callback (montaje)
    // fuerza un reflow sincrónico de toda la tabla ("Forced reflow" de decenas
    // de ms con tablas grandes). ResizeObserver se encarga de lo que cambie.
    requestAnimationFrame(update)
    const observer = new ResizeObserver(update)
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  }

  const actionsOnLeft = createMemo(() => !!merged.actions && merged.actionsPosition !== 'right')

  /**
   * Ancho real de la columna de checkboxes. Igual que `actionsWidth`: se mide en
   * el DOM porque las columnas fijas (acciones a la izquierda, fijadas a mano)
   * necesitan saber cuánto desplazarse para quedar justo después, sin solaparse
   * ni dejar un hueco de subpíxel entre columnas fijas.
   */
  const [selectableWidth, setSelectableWidth] = createSignal(52)

  const measureSelectable = (el: HTMLTableCellElement) => {
    const update = () => setSelectableWidth(el.getBoundingClientRect().width)
    requestAnimationFrame(update)
    const observer = new ResizeObserver(update)
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  }

  /** Ancho visible de la tabla. De él depende cuánto se puede congelar. */
  const [containerWidth, setContainerWidth] = createSignal(0)

  let contenedorEl: HTMLDivElement | undefined

  const measureContainer = (el: HTMLDivElement) => {
    contenedorEl = el
    const update = () => setContainerWidth(el.clientWidth)
    requestAnimationFrame(update)
    const observer = new ResizeObserver(update)
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  }

  /**
   * Cuánto ancho puede ocupar lo congelado. En pantallas pequeñas es cero: con
   * dos o tres columnas fijas no quedaría nada que desplazar, que es justo el
   * problema que tienen las tablas en el móvil.
   */
  const stickyBudget = createMemo(() => (containerWidth() > 0 && containerWidth() < 640 ? 0 : containerWidth() * 0.6))

  const actionsFixed = createMemo(() => !!merged.actions && merged.actionsSticky !== false && stickyBudget() > 0)

  /** La columna de checkboxes se congela a la izquierda, igual que las acciones. */
  const selectableFixed = createMemo(() => !!merged.selectable && stickyBudget() > 0)

  /** Estilo de la celda de checkbox cuando está fijada a la izquierda. */
  const selectableStickyStyle = (isHeader = false): JSX.CSSProperties => {
    if (!selectableFixed()) return {}
    return {
      position: 'sticky',
      left: '0px',
      'z-index': isHeader ? 25 : 10
    }
  }

  /** Lo que ocupa la columna de acciones cuando va fija a la izquierda. */
  const leadingOffset = createMemo(() => (actionsOnLeft() && actionsFixed() ? actionsWidth() : 0))

  /** Estilo de la celda de acciones cuando está fijada a un lado. */
  const actionsStickyStyle = (isHeader = false): JSX.CSSProperties => {
    if (!actionsFixed()) return {}
    return {
      position: 'sticky',
      'z-index': isHeader ? 25 : 10,
      ...(actionsOnLeft() ? { left: `${merged.selectable ? selectableWidth() : 0}px` } : { right: '0px' })
    }
  }

  /** Ancho mínimo de una columna: el suyo si lo declara, si no el de la tabla. */
  const columnMinWidthStyle = (column: ColumnType<T>): JSX.CSSProperties => {
    const value = column.minWidth ?? merged.columnMinWidth
    if (!value) return {}
    return { 'min-width': typeof value === 'number' ? `${value}px` : value }
  }

  /**
   * Columnas fijadas por el usuario desde la cabecera. `null` significa
   * "desfijada a mano", y por eso gana sobre el `sticky` que traiga la columna.
   */
  const [pinnedColumns, setPinnedColumns] = createStore<Record<string, StickySideType | null>>({})

  /** Identificador estable de una columna, para recordar si está fijada. */
  const columnKey = (column: ColumnType<T>, index: number): string => getColumnSortKey(column) ?? (typeof column.accessor === 'string' ? column.accessor : `col-${index}`)

  /** Lado en el que queda fijada una columna, mirando primero lo que eligió el usuario. */
  const stickyOf = (column: ColumnType<T>, index: number): StickySideType | undefined => {
    const chosen = pinnedColumns[columnKey(column, index)]
    if (chosen === null) return undefined
    return chosen ?? column.sticky
  }

  const widthOfColumn = (column: ColumnType<T>): number => {
    const value = column.minWidth ?? merged.columnMinWidth ?? 150
    return typeof value === 'number' ? value : Number.parseInt(value, 10) || 0
  }

  /**
   * De lo que el usuario pidió congelar, lo que de verdad cabe. Se recorre por
   * orden (y desde el final para la derecha) y se deja de congelar en cuanto se
   * agota el presupuesto, para que siempre quede tabla que desplazar.
   */
  const appliedSticky = createMemo(() => {
    const aplicadas = new Map<number, StickySideType>()
    const budget = stickyBudget()
    if (budget <= 0) return aplicadas

    let usado = (selectableFixed() ? selectableWidth() : 0) + (actionsFixed() ? actionsWidth() : 0)

    for (let i = 0; i < merged.columns.length; i++) {
      if (stickyOf(merged.columns[i], i) !== 'left') continue
      const ancho = widthOfColumn(merged.columns[i])
      if (usado + ancho > budget) break
      usado += ancho
      aplicadas.set(i, 'left')
    }

    for (let i = merged.columns.length - 1; i >= 0; i--) {
      if (stickyOf(merged.columns[i], i) !== 'right') continue
      const ancho = widthOfColumn(merged.columns[i])
      if (usado + ancho > budget) break
      usado += ancho
      aplicadas.set(i, 'right')
    }

    return aplicadas
  })

  /** Lado en el que queda pintada la columna (puede no aplicarse si no cabe). */
  const stickySideOf = (index: number): StickySideType | undefined => appliedSticky().get(index)

  const togglePin = (column: ColumnType<T>, index: number, side: StickySideType) => {
    const key = columnKey(column, index)
    setPinnedColumns(key, stickyOf(column, index) === side ? null : side)
  }

  const getStickyStyle = (_column: ColumnType<T>, index: number, isHeader = false): JSX.CSSProperties => {
    const side = stickySideOf(index)
    if (!side) return {}

    const style: JSX.CSSProperties = {
      position: 'sticky',
      'z-index': isHeader ? 25 : 10
    }

    if (side === 'left') {
      let offset = (merged.selectable ? selectableWidth() : 0) + leadingOffset()
      for (let i = 0; i < index; i++) {
        if (stickySideOf(i) === 'left') offset += widthOfColumn(merged.columns[i])
      }
      style.left = `${offset}px`
    } else {
      // A la derecha se acumulan las que quedan por detrás, más las acciones.
      let offset = !actionsOnLeft() && actionsFixed() ? actionsWidth() : 0
      for (let i = merged.columns.length - 1; i > index; i--) {
        if (stickySideOf(i) === 'right') offset += widthOfColumn(merged.columns[i])
      }
      style.right = `${offset}px`
    }

    return style
  }

  const getCellValue = (item: T, column: ColumnType<T>): JSX.Element => {
    if (typeof column.accessor === 'function') {
      return column.accessor(item)
    }
    const data = accessJson(item, String(column.accessor))
    if (typeof data === 'string' || typeof data === 'number') {
      return data as unknown as JSX.Element
    }
    return JSON.stringify(data, null, 2) as unknown as JSX.Element
  }

  /* ── Cabecera flotante (`headSticky`) ───────────────────────────────────── */

  let tablaEl: HTMLTableElement | undefined
  let cabeceraEl: HTMLTableSectionElement | undefined
  let flotanteEl: HTMLDivElement | undefined

  const [flotante, setFlotante] = createSignal({ top: 0, left: 0, width: 0, visible: false })
  const [anchosColumna, setAnchosColumna] = createSignal<number[]>([])
  const [anchoTabla, setAnchoTabla] = createSignal(0)

  /** Altura del header normal (con columnas). La fila de selección la copia
   *  para no cambiar el alto de la tabla al seleccionar filas. */
  const [theadHeight, setTheadHeight] = createSignal(0)

  /** Contenedor con scroll vertical más cercano: es quien marca dónde se queda la cabecera. */
  const contenedorScroll = (el: HTMLElement | undefined): HTMLElement | null => {
    let actual = el?.parentElement ?? null
    while (actual) {
      const overflow = getComputedStyle(actual).overflowY
      if (overflow === 'auto' || overflow === 'scroll') return actual
      actual = actual.parentElement
    }
    return null
  }

  /** Altura a la que se ancla la cabecera: bajo la barra superior del panel. */
  const topeSuperior = (): number => {
    if (merged.headStickyOffset !== undefined) return merged.headStickyOffset
    const contenedor = contenedorScroll(contenedorEl)
    return contenedor ? contenedor.getBoundingClientRect().top : 0
  }

  /**
   * Anchos reales de cada columna. Se leen de la primera fila del cuerpo porque
   * siempre tiene una celda por columna; la cabecera puede llevar `colspan` o
   * `rowspan` cuando hay grupos y no serviría de referencia.
   */
  const medirAnchos = (): void => {
    if (!tablaEl || !cabeceraEl) return
    const primeraDelCuerpo = tablaEl.tBodies[0]?.rows[0]
    const fila = primeraDelCuerpo && !Array.from(primeraDelCuerpo.cells).some(celda => celda.colSpan > 1) ? primeraDelCuerpo : cabeceraEl.rows[cabeceraEl.rows.length - 1]
    if (!fila) return
    setAnchosColumna(Array.from(fila.cells).map(celda => celda.getBoundingClientRect().width))
    setAnchoTabla(tablaEl.getBoundingClientRect().width)
  }

  let recalculoPendiente = false

  // Medir la altura del header normal (sin selección) para que la fila de
  // selección ocupe lo mismo y la tabla no salte al marcar filas. Se re-mide
  // cuando cambia el modo denso o el tamaño de la ventana.
  const medirTheadHeight = (): void => {
    if (selected.size > 0) return
    const h = cabeceraEl?.getBoundingClientRect().height
    if (h && h !== theadHeight()) setTheadHeight(h)
  }

  createEffect(() => {
    void state.dense
    medirTheadHeight()
    window.addEventListener('resize', medirTheadHeight)
    onCleanup(() => window.removeEventListener('resize', medirTheadHeight))
  })

  const actualizarFlotante = (): void => {
    if (recalculoPendiente) return
    recalculoPendiente = true
    requestAnimationFrame(() => {
      recalculoPendiente = false
      if (!merged.headSticky || !contenedorEl || !tablaEl || !cabeceraEl) return

      const caja = contenedorEl.getBoundingClientRect()
      const tabla = tablaEl.getBoundingClientRect()
      const cabecera = cabeceraEl.getBoundingClientRect()
      const tope = topeSuperior()

      // Sólo mientras la cabecera real ya se fue por arriba y aún queda tabla debajo.
      const visible = cabecera.top < tope && tabla.bottom > tope + cabecera.height
      setFlotante({ top: tope, left: caja.left, width: caja.width, visible })

      if (visible) {
        medirAnchos()
        // El desplazamiento horizontal se replica con `scrollLeft` (y no con una
        // transformación) para que las columnas fijas sigan funcionando en la copia.
        if (flotanteEl) flotanteEl.scrollLeft = contenedorEl.scrollLeft
      }
    })
  }

  createEffect(() => {
    if (!merged.headSticky) return
    // En captura para enterarse también del scroll de contenedores internos.
    window.addEventListener('scroll', actualizarFlotante, true)
    window.addEventListener('resize', actualizarFlotante)
    const observer = new ResizeObserver(actualizarFlotante)
    if (tablaEl) observer.observe(tablaEl)
    actualizarFlotante()
    onCleanup(() => {
      window.removeEventListener('scroll', actualizarFlotante, true)
      window.removeEventListener('resize', actualizarFlotante)
      observer.disconnect()
    })
  })

  // Cambiar de página, ordenar o filtrar cambia el alto de la tabla.
  createEffect(() => {
    displayedData()
    if (merged.headSticky) actualizarFlotante()
  })

  /**
   * Filas de la cabecera. Es una función porque `headSticky` la pinta dos veces:
   * la real dentro de la tabla y una copia en la capa flotante. Al ser el mismo
   * marcado, la copia ordena y fija columnas igual que la original.
   *
   * `esClon` evita que la copia vuelva a medir la columna de acciones: mediría
   * su propio ancho (el de la copia) y pisaría el de la tabla real.
   */
  const renderHead = (esClon = false): JSX.Element => (
    <>
      <Show
        when={selected.size > 0}
        fallback={
          <>
            {/* Fila de grupos de columnas */}
            <Show when={merged.columnGroups && merged.columnGroups.length > 0}>
              <tr>
                <Show when={merged.selectable}>
                  <th rowspan={2} class={getThClass()('w-0 text-nowrap')} style={selectableStickyStyle(true)} />
                </Show>
                <For each={merged.columnGroups}>
                  {group => (
                    <th colspan={group.colspan} class={twMerge(getThClass()('text-center border-b border-border-strong'), group.class)}>
                      {group.title}
                    </th>
                  )}
                </For>
              </tr>
            </Show>

            <tr>
              {/* Columna de selección */}
              <Show when={merged.selectable}>
                <th ref={esClon ? undefined : measureSelectable} scope="col" class={getThClass()('w-0 text-nowrap')} style={selectableStickyStyle(true)}>
                  <div class="flex items-center">
                    <Checkbox color={selectableColor()} checked={allSelected()} indeterminate={indeterminate()} onChange={handleSelectAll} />
                  </div>
                </th>
              </Show>

              {/* Columna de acciones fijada a la izquierda */}
              <Show when={merged.actions && actionsOnLeft()}>
                <th ref={esClon ? undefined : measureActions} scope="col" class={getThClass()(twMerge('w-0 text-nowrap', actionsFixed() ? 'bg-surface-muted' : ''))} style={actionsStickyStyle(true)}>
                  <span>{merged.actionsLabel}</span>
                </th>
              </Show>

              {/* Columnas de la tabla */}
              <For each={merged.columns}>
                {(column, index) => {
                  const sortKey = getColumnSortKey(column)
                  const isSorted = createMemo(() => sortKey && state.sortColumn === sortKey)
                  const sortDirection = createMemo(() => (isSorted() ? state.sortDirection : null))

                  return (
                    <th
                      {...ObjExcept(column, ['header', 'accessor', 'cellClass', 'sortable', 'sortKey', 'sticky', 'minWidth', 'group', 'pinnable'])}
                      scope="col"
                      class={twMerge(
                        getThClass()(column.class),
                        stickySideOf(index()) === 'left' ? 'bg-surface-muted' : '',
                        stickySideOf(index()) === 'right' ? 'bg-surface-muted' : '',
                        column.sortable ? 'cursor-pointer select-none hover:text-foreground transition-colors' : ''
                      )}
                      style={{
                        ...columnMinWidthStyle(column),
                        ...getStickyStyle(column, index(), true)
                      }}
                      onClick={column.sortable ? () => handleSortChange(column) : undefined}
                    >
                      <div
                        class={twMerge('group/th flex items-center gap-2', column.class?.includes('text-right') ? 'justify-end' : '', column.class?.includes('text-center') ? 'justify-center' : '')}
                      >
                        <span>{column.header}</span>

                        {/* Fijar la columna: se elige el lado desde la propia cabecera. */}
                        <Show when={merged.pinnable !== false && column.pinnable !== false}>
                          <span onClick={e => e.stopPropagation()}>
                            <Menu
                              trigger={
                                <span
                                  class={twMerge(
                                    'inline-flex h-6 w-6 items-center justify-center rounded transition-all cursor-pointer hover:bg-surface-hover',
                                    stickyOf(column, index()) ? 'text-primary opacity-100' : 'text-foreground-subtle opacity-0 group-hover/th:opacity-100'
                                  )}
                                  title={stickyOf(column, index()) ? 'Columna fijada' : 'Fijar columna'}
                                >
                                  <Icon icon={stickyOf(column, index()) ? 'material-symbols-light:push-pin' : 'material-symbols-light:push-pin-outline'} size={16} />
                                </span>
                              }
                              items={[
                                {
                                  label: stickyOf(column, index()) === 'left' ? 'Quitar de la izquierda' : 'Fijar a la izquierda',
                                  icon: 'material-symbols-light:align-horizontal-left',
                                  onClick: () => togglePin(column, index(), 'left')
                                },
                                {
                                  label: stickyOf(column, index()) === 'right' ? 'Quitar de la derecha' : 'Fijar a la derecha',
                                  icon: 'material-symbols-light:align-horizontal-right',
                                  onClick: () => togglePin(column, index(), 'right')
                                }
                              ]}
                            />
                          </span>
                        </Show>

                        <Show when={column.sortable}>
                          <span class={twMerge('flex flex-col text-foreground-subtle', isSorted() ? 'text-primary' : '')}>
                            <Show when={sortDirection()} fallback={<Icon icon="material-symbols-light:sort" size={16} class="opacity-50" />}>
                              <Icon icon={sortDirection() === 'asc' ? 'material-symbols-light:sort' : 'material-symbols-light:sort'} size={16} />
                            </Show>
                          </span>
                        </Show>
                      </div>
                    </th>
                  )
                }}
              </For>
              {/* Columna de acciones a la derecha (si así se pidió) */}
              <Show when={merged.actions && !actionsOnLeft()}>
                <th scope="col" class={getThClass()(twMerge('w-0 text-nowrap', actionsFixed() ? 'bg-surface-muted' : ''))} style={actionsStickyStyle(true)}>
                  <span>{merged.actionsLabel}</span>
                </th>
              </Show>
            </tr>
          </>
        }
      >
        {/* Fila de selección masiva en el header */}
        <tr>
          <th colspan={totalColumns()} class="relative overflow-hidden bg-surface p-0" style={theadHeight() ? { height: `${theadHeight()}px` } : undefined}>
            {/* El tinte *-soft es translúcido en modo oscuro: va como capa sobre
                `bg-surface` (opaco) para que al quedar fija la fila no se
                transparente el contenido que pasa por debajo al hacer scroll. */}
            <div class={twMerge('absolute inset-0', BANNER_COLORS[selectableColor()])} />
            <div class="relative z-10 flex items-center gap-3 px-4">
              <Checkbox color={selectableColor()} checked={selected.size > 0} indeterminate={indeterminate()} onChange={() => selected.clear()} />
              <span class="text-sm font-semibold">
                {selected.size} {selected.size === 1 ? 'seleccionado' : 'seleccionados'}
              </span>
              <div class="flex-1" />
              <div class="flex items-center gap-2 text-nowrap">
                {merged.selectableAction?.(selected)}
                <Show when={merged.onDeleteSelected}>
                  <Tooltip content="Eliminar selección">
                    <Button variant="text" size="sm" color="danger" icon="material-symbols-light:delete-outline" onClick={() => merged.onDeleteSelected?.()} aria-label="Eliminar selección" />
                  </Tooltip>
                </Show>
              </div>
            </div>
          </th>
        </tr>
      </Show>
    </>
  )

  const showToolbar = createMemo(() => merged.searchable || merged.toolbarActions)
  const showTabs = createMemo(() => merged.showFilters && (merged.filterOptions?.length ?? 0) > 0)

  return (
    <Card class={twMerge('overflow-hidden', merged.class)}>
      {/* Header de la tabla. En móvil el título y las acciones se apilan: en la
          misma fila, los botones estrujan el título hasta partirlo en cuatro. */}
      <Show when={merged.renderTopHeader || merged.title || merged.subtitle || merged.headerActions}>
        <Show
          when={merged.renderTopHeader}
          fallback={
            <div class="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-border">
              <div class="min-w-0">
                <Show when={merged.title}>
                  <h2 class="font-semibold text-lg text-foreground">{merged.title}</h2>
                </Show>
                <Show when={merged.subtitle}>
                  <p class="text-sm text-foreground-subtle mt-0.5">{merged.subtitle}</p>
                </Show>
              </div>
              <Show when={merged.headerActions}>
                <div class="flex items-center gap-2 flex-wrap shrink-0 [&>button]:grow sm:[&>button]:grow-0">{merged.headerActions}</div>
              </Show>
            </div>
          }
        >
          {merged.renderTopHeader}
        </Show>
      </Show>

      {/* Chips de filtros activos: dentro de la tarjeta, justo sobre la tabla.
          Van aquí (no debajo de la tabla) para que se vean con las filas. */}
      <Show when={(merged.filterChips?.length ?? 0) > 0}>
        <div class="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border">
          <For each={merged.filterChips as FilterChipItemType[]}>
            {chip => (
              <Chip size="sm" color={chip.color ?? 'default'} variant="soft" removable onRemove={chip.onRemove} icon={chip.icon}>
                {chip.label}
              </Chip>
            )}
          </For>
        </div>
      </Show>

      {/* Tabs de filtro con contador */}
      <Show when={showTabs()}>
        <div class="flex items-center gap-1 px-2 border-b border-border overflow-x-auto hide-scrollbar">
          <For each={filterTabs()}>
            {tab => {
              const isActive = createMemo(() => activeTab() === tab.value)
              return (
                <button
                  type="button"
                  onClick={() => handleFilterClick(tab.value)}
                  class={twMerge(
                    'relative flex items-center gap-2 px-3 py-3.5 text-sm font-medium whitespace-nowrap transition-colors',
                    isActive() ? 'text-foreground' : 'text-foreground-subtle hover:text-foreground'
                  )}
                >
                  <span>{tab.label}</span>
                  <Show when={tab.count !== undefined}>
                    <Chip variant="soft" color={tab.color ?? (isActive() ? 'primary' : 'secondary')} size="sm" rounded>
                      {tab.count}
                    </Chip>
                  </Show>
                  <span class={twMerge('absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-foreground transition-opacity', isActive() ? 'opacity-100' : 'opacity-0')} />
                </button>
              )
            }}
          </For>
        </div>
      </Show>

      {/* Toolbar: búsqueda + acciones */}
      <Show when={showToolbar()}>
        <div class="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <Show when={merged.searchable}>
            <div class="relative w-full sm:max-w-xs">
              <Icon icon="material-symbols-light:search" size={18} class="absolute start-3 top-1/2 -translate-y-1/2 text-foreground-subtle pointer-events-none" />
              <input
                type="search"
                value={searchQuery()}
                placeholder={merged.searchPlaceholder}
                onInput={e => handleSearchInput(e.currentTarget.value)}
                class="w-full rounded-lg border border-border bg-surface-muted py-2.5 ps-10 pe-3 text-sm text-foreground placeholder:text-foreground-subtle outline-none transition-colors focus:border-primary"
              />
            </div>
          </Show>
          <Show when={merged.toolbarActions}>
            <div class="flex items-center gap-1">{merged.toolbarActions}</div>
          </Show>
        </div>
      </Show>

      {/* Contenedor de la tabla */}
      <div ref={measureContainer} class="overflow-auto relative" style={containerStyle()}>
        {/* Overlay de loading */}
        <Show when={merged.loading}>
          <div class="absolute inset-0 bg-surface/60 z-30 flex items-center justify-center backdrop-blur-sm">
            <div class="flex flex-col items-center gap-3">
              <Icon icon="material-symbols-light:refresh" size={40} class="animate-spin text-primary" />
              <span class="text-sm text-foreground-muted">Cargando...</span>
            </div>
          </div>
        </Show>

        <table ref={tablaEl} class="min-w-full border-separate border-spacing-0">
          <thead ref={cabeceraEl} class={twMerge('relative', merged.stickyHeader || merged.maxHeight ? 'sticky top-0 z-20' : '')}>
            {renderHead()}
          </thead>

          <tbody>
            {/* Loading skeleton */}
            <Show when={merged.loading && displayedData().length === 0}>
              <For each={Array.from({ length: Math.min(state.currentPerPage, 5) })}>
                {() => (
                  <tr class={getTrClass()()}>
                    <Show when={merged.selectable}>
                      <td class={twMerge(TD_BASE_CLASS, state.dense ? 'py-2' : '', selectableFixed() ? 'bg-surface' : '')} style={selectableStickyStyle()}>
                        <Skeleton class="h-5 w-5 rounded" />
                      </td>
                    </Show>
                    <For each={merged.columns}>
                      {() => (
                        <td class={twMerge(TD_BASE_CLASS, state.dense ? 'py-2' : '')}>
                          <Skeleton class="h-4 w-full max-w-50" />
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </Show>

            {/* Datos de la tabla */}
            <For
              each={displayedData()}
              fallback={
                <Show when={!merged.loading}>
                  <tr>
                    <td colspan={totalColumns()} class="text-center p-4">
                      <div class="flex flex-col items-center justify-center p-10 text-foreground-subtle bg-surface-muted/50 rounded-lg">
                        <Icon icon="material-symbols-light:inventory-2-outline" size={80} class="opacity-50" />
                        <div class="text-md font-medium mt-4">{merged.emptyMessage || 'No hay datos disponibles'}</div>
                      </div>
                    </td>
                  </tr>
                </Show>
              }
            >
              {(item, index) => {
                const itemKey = item[merged.keyField as keyof T]

                return (
                  <tr
                    class={getTrClass()(
                      twMerge(
                        isRowSelected(itemKey) ? twMerge(TR_SELECTED_COLORS[selectableColor()], merged.selectableClass) : '',
                        merged.striped && index() % 2 === 1 ? 'bg-surface-muted/40' : '',
                        merged.onRowClick ? 'cursor-pointer' : '',
                        merged.rowClass?.(item, index()) ?? ''
                      )
                    )}
                    onClick={merged.onRowClick ? () => merged.onRowClick?.(item) : undefined}
                  >
                    {/* Checkbox de selección */}
                    <Show when={merged.selectable}>
                      <td class={twMerge('w-0 text-nowrap', TD_BASE_CLASS, state.dense ? 'py-2' : '', selectableFixed() ? 'bg-surface' : '')} style={selectableStickyStyle()}>
                        <div class="flex items-center" onClick={e => e.stopPropagation()}>
                          <Checkbox color={selectableColor()} checked={isRowSelected(itemKey)} onChange={() => handleRowToggle(itemKey)} />
                        </div>
                      </td>
                    </Show>

                    {/* Celda de acciones fijada a la izquierda */}
                    <Show when={merged.actions && actionsOnLeft()}>
                      <td
                        class={twMerge('w-0 text-nowrap', TD_BASE_CLASS, state.dense ? 'py-2' : '', actionsFixed() ? 'bg-surface' : '')}
                        style={actionsStickyStyle()}
                        onClick={e => e.stopPropagation()}
                      >
                        {merged.actions!(item)}
                      </td>
                    </Show>

                    {/* Celdas de datos */}
                    <For each={merged.columns}>
                      {(column, colIndex) => (
                        <td
                          class={twMerge(
                            TD_BASE_CLASS,
                            state.dense ? 'py-2' : '',
                            column.cellClass,
                            stickySideOf(colIndex()) === 'left' ? 'bg-surface' : '',
                            stickySideOf(colIndex()) === 'right' ? 'bg-surface' : ''
                          )}
                          style={{
                            ...columnMinWidthStyle(column),
                            ...getStickyStyle(column, colIndex())
                          }}
                        >
                          {getCellValue(item, column)}
                        </td>
                      )}
                    </For>
                    {/* Celda de acciones a la derecha (si así se pidió) */}
                    <Show when={merged.actions && !actionsOnLeft()}>
                      <td class={twMerge('w-0 text-nowrap', TD_BASE_CLASS, state.dense ? 'py-2' : '', actionsFixed() ? 'bg-surface' : '')} style={actionsStickyStyle()}>
                        {merged.actions!(item)}
                      </td>
                    </Show>
                  </tr>
                )
              }}
            </For>
          </tbody>
        </table>
      </div>

      {/* Cabecera flotante: copia fija de la cabecera real.
          Hace falta porque el contenedor de la tabla tiene scroll horizontal, y eso
          lo convierte también en contenedor de scroll vertical: un `position: sticky`
          se quedaría anclado a él y se iría con la página igualmente. La copia se
          coloca sobre el borde superior del panel que sí scrollea. */}
      <Show when={merged.headSticky && flotante().visible}>
        <div ref={flotanteEl} class="fixed z-20 overflow-hidden shadow-sm" style={{ top: `${flotante().top}px`, left: `${flotante().left}px`, width: `${flotante().width}px` }}>
          {/* `table-layout: fixed` + colgroup con los anchos medidos: es lo que
              mantiene la copia alineada columna a columna con la tabla real. */}
          <table class="border-separate border-spacing-0" style={{ 'table-layout': 'fixed', width: `${anchoTabla()}px` }}>
            <colgroup>
              <For each={anchosColumna()}>{ancho => <col style={{ width: `${ancho}px` }} />}</For>
            </colgroup>
            <thead>{renderHead(true)}</thead>
          </table>
        </div>
      </Show>

      {/* Footer con paginación */}
      <Show when={merged.pagination}>
        <div class="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border">
          {/* Lado izquierdo: Switch dense + Info de paginación */}
          <div class="flex items-center gap-4">
            <Show when={merged.showDense}>
              <div class="flex items-center gap-2 text-sm text-foreground-subtle select-none">
                <Switch size="sm" checked={state.dense} onChange={({ target }) => handleDenseChange(target.checked)} />
                <span>Compacto</span>
              </div>
            </Show>

            <Show when={!merged.hidePaginationInfo}>
              <span class="text-sm text-foreground-subtle">{merged.paginationText ? merged.paginationText(paginationInfo()) : defaultPaginationText(paginationInfo())}</span>
            </Show>
          </div>

          {/* Lado derecho: Selector de perPage + Controles de página */}
          <div class="flex items-center gap-4">
            {/* Selector de elementos por página */}
            <Show when={!merged.hidePerPageSelector}>
              <div class="flex items-center gap-2">
                <span class="text-sm text-foreground-subtle hidden sm:inline">Filas:</span>
                {/* Misma talla que los botones de página: comparten fila. */}
                <Select size="sm" value={state.currentPerPage} options={perPageSelectOptions()} onChange={value => handlePerPageChange(value as number)} class="w-auto min-w-25" />
              </div>
            </Show>

            {/* Controles de navegación */}
            <div class="flex items-center gap-1">
              <Tooltip content="Primera página">
                <Button
                  variant="text"
                  size="sm"
                  color="default"
                  icon="material-symbols-light:keyboard-double-arrow-left"
                  disabled={!hasPrevPage() || merged.loading}
                  onClick={goToFirstPage}
                  aria-label="Primera página"
                />
              </Tooltip>

              <Tooltip content="Página anterior">
                <Button
                  variant="text"
                  size="sm"
                  color="default"
                  icon="material-symbols-light:keyboard-arrow-left"
                  disabled={!hasPrevPage() || merged.loading}
                  onClick={goToPrevPage}
                  aria-label="Página anterior"
                />
              </Tooltip>

              <div class="px-3 py-1 text-sm font-medium text-foreground-muted bg-surface-muted rounded-md min-w-20 text-center">
                {paginationInfo().page} / {paginationInfo().totalPages}
              </div>

              <Tooltip content="Página siguiente">
                <Button
                  variant="text"
                  size="sm"
                  color="default"
                  icon="material-symbols-light:keyboard-arrow-right"
                  disabled={!hasNextPage() || merged.loading}
                  onClick={goToNextPage}
                  aria-label="Página siguiente"
                />
              </Tooltip>

              <Tooltip content="Última página">
                <Button
                  variant="text"
                  size="sm"
                  color="default"
                  icon="material-symbols-light:keyboard-double-arrow-right"
                  disabled={!hasNextPage() || merged.loading}
                  onClick={goToLastPage}
                  aria-label="Última página"
                />
              </Tooltip>
            </div>
          </div>
        </div>
      </Show>
    </Card>
  )
}

/* ─────────────────────────────────────────────────────────────────────────
 * Celdas helper: componer tablas tipo "Minimal UI" sin repetir markup.
 * ──────────────────────────────────────────────────────────────────────── */

export interface TableMediaProps {
  /** URL de la imagen/thumbnail. */
  src?: string
  /** Icono (Iconify) como alternativa a la imagen. */
  icon?: string
  /** Color del recuadro del icono. */
  iconColor?: MainColorsType
  title: JSX.Element
  subtitle?: JSX.Element
  /** Forma del thumbnail (default: rounded). */
  rounded?: 'lg' | 'full'
  class?: string
}

/** Celda con thumbnail/icono + título + subtítulo (producto, usuario…). */
const TableMedia = (props: TableMediaProps): JSX.Element => {
  const radius = () => (props.rounded === 'full' ? 'rounded-full' : 'rounded-lg')
  return (
    <div class={twMerge('flex items-center gap-3', props.class)}>
      <Show when={props.src || props.icon} fallback={typeof props.title === 'string' ? <Avatar alt={props.title} size="md" /> : null}>
        <Show
          when={props.src}
          fallback={
            <div class={twMerge('flex h-12 w-12 shrink-0 items-center justify-center', radius(), BANNER_COLORS[props.iconColor ?? 'primary'])}>
              <Icon icon={props.icon!} size={24} />
            </div>
          }
        >
          <img src={props.src} alt={typeof props.title === 'string' ? props.title : ''} class={twMerge('h-12 w-12 shrink-0 object-cover', radius())} />
        </Show>
      </Show>
      <div class="min-w-0">
        <div class="font-semibold text-foreground truncate">{props.title}</div>
        <Show when={props.subtitle}>
          <div class="text-xs text-foreground-subtle truncate">{props.subtitle}</div>
        </Show>
      </div>
    </div>
  )
}

export interface TableProgressProps {
  /** Valor actual. */
  value: number
  /** Máximo (default: 100). */
  max?: number
  /** Etiqueta bajo la barra (p.ej. "72 en stock"). */
  label?: JSX.Element
  color?: MainColorsType
  class?: string
}

/** Celda de barra de progreso/stock con etiqueta. */
const TableProgress = (props: TableProgressProps): JSX.Element => {
  const pct = () => {
    const max = props.max ?? 100
    if (max <= 0) return 0
    return Math.min(100, Math.max(0, (props.value / max) * 100))
  }
  return (
    <div class={twMerge('min-w-[120px] space-y-1.5', props.class)}>
      <div class="h-1.5 w-full max-w-[88px] overflow-hidden rounded-full bg-surface-hover">
        <div class={twMerge('h-full rounded-full transition-[width] duration-300', SOLID_BG[props.color ?? 'success'])} style={{ width: `${pct()}%` }} />
      </div>
      <Show when={props.label}>
        <div class="text-xs text-foreground-subtle">{props.label}</div>
      </Show>
    </div>
  )
}

export interface TableStatusProps {
  children: JSX.Element
  color?: MainColorsType
  variant?: 'soft' | 'filled' | 'outlined'
}

/** Celda de estado: píldora suave (Draft/Published/Pending…). */
const TableStatus = (props: TableStatusProps): JSX.Element => (
  <Chip color={props.color ?? 'default'} variant={props.variant ?? 'soft'} size="sm" rounded>
    {props.children}
  </Chip>
)

const TableNamespace = Object.assign(Table, {
  Media: TableMedia,
  Progress: TableProgress,
  Status: TableStatus
})

export default TableNamespace as typeof Table & {
  Media: typeof TableMedia
  Progress: typeof TableProgress
  Status: typeof TableStatus
}
